import { useContext, useEffect, useCallback, useMemo } from "react";
import styles from "./TasksOverview.module.css";
import { TasksOverviewTabs } from "./Tabs/TasksOverviewTabs";
import { useTranslation } from "@coworker/locales";
import { TasksTabs } from "../constants/tasksTabs";
import FixaButton from "../../shared/wrappers/FixaButton/FixaButton";
import StackIconPath from "../../shared/wrappers/FixaSSRIcon/paths/StackIconPath";
import { ModalContext } from "../../layout/context/ModalContextProvider";
import { ModalType } from "../../layout/context/modalType";
import trackerHelper from "@coworker/app/src/helpers/tracker";
import { TaskType } from "../../tasks/enums/taskTypes";
import { useTask } from "../hooks/useTask";
import { useQueryClient } from "@tanstack/react-query";
import { TabPanel } from "./TabPanel/TabPanel";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { useTasksOverviewTabs } from "../hooks/useTasksOverviewTabs";
import { FilterPanel } from "../../shared/filters/components/FilterPanel";
import useSearchParamState from "../../shared/hooks/useSearchParamState";
import {
  IN_PROGRESS_TASKS_QUERY_KEY,
  OPEN_TASKS_QUERY_KEY,
  OVERVIEW_TASKS_QUERY_KEY,
} from "../constants/queryKeys";

export const TasksOverview = () => {
  const { toggleModal } = useContext(ModalContext);
  const [
    { taskDetails: taskDetailsUrlParameter, tab: tabParam },
    setSearchParams,
  ] = useSearchParamState();
  const { data: taskDetails } = useTask(taskDetailsUrlParameter);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const tasksOverviewTabs = useTasksOverviewTabs();

  const activeTab = useMemo(() => {
    const normalizedTab = tabParam?.toUpperCase();
    return normalizedTab && normalizedTab in TasksTabs
      ? TasksTabs[normalizedTab as keyof typeof TasksTabs]
      : TasksTabs.OPEN;
  }, [tabParam]);

  const handleTabChange = useCallback(
    (newTab: TasksTabs) => {
      setSearchParams((params) => {
        params.set("tab", newTab.toLowerCase());
      });
    },
    [setSearchParams]
  );

  const activeTabPanel = useMemo(() => {
    return tasksOverviewTabs.find((tab) => tab.key === activeTab);
  }, [activeTab, tasksOverviewTabs]);

  const onShowTaskDetails = useCallback(
    (taskId: string, taskType: TaskType) => {
      toggleModal({
        type: ModalType.TaskDetail,
        payload: { taskId, taskType },
      });
    },
    [toggleModal]
  );

  useEffect(() => {
    if (!tabParam) {
      setSearchParams((params) => {
        params.set("tab", TasksTabs.OPEN.toLowerCase());
      });
    }
  }, [tabParam, setSearchParams]);

  useEffect(() => {
    if (taskDetailsUrlParameter && taskDetails) {
      const taskType = taskDetails.task_type;
      queryClient.invalidateQueries({
        queryKey: [OVERVIEW_TASKS_QUERY_KEY, IN_PROGRESS_TASKS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [OVERVIEW_TASKS_QUERY_KEY, OPEN_TASKS_QUERY_KEY],
      });
      onShowTaskDetails(taskDetailsUrlParameter, taskType);
    }
  }, [taskDetailsUrlParameter, taskDetails, onShowTaskDetails, queryClient]);

  useEffect(() => {
    if (taskDetailsUrlParameter && taskDetails) {
      setSearchParams((params) => {
        params.delete("taskDetails");
      });
    }
  }, [taskDetailsUrlParameter, taskDetails, setSearchParams]);

  return (
    <div className={styles["pageContainer"]}>
      <div className={styles["topWrapper"]}>
        <FixaText
          textStyle="Heading.L"
          children={t("TasksString")}
          className={styles["title"]}
        />
        {(activeTab === TasksTabs.OPEN ||
          activeTab === TasksTabs.IN_PROGRESS) && (
          <FixaButton
            iconOnly
            ssrIcon={StackIconPath}
            size="small"
            type="emphasised"
            className={styles["multiSelectButton"]}
            onClick={() => {
              trackerHelper.trackMultiSelectButtonTab(activeTab);
              toggleModal({
                type: ModalType.MultiSelectTasks,
                payload: {
                  type: activeTab,
                },
              });
            }}
          />
        )}
        <TasksOverviewTabs
          tabs={tasksOverviewTabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </div>
      {activeTabPanel && (
        <>
          <div className={styles["filterPanelWrapper"]}>
            <FilterPanel
              key={`filter-${activeTabPanel.key}`}
              filterValues={activeTabPanel.filters.filterValues}
              setFilterValues={activeTabPanel.filters.setFilterValues}
              useFiltersFactory={activeTabPanel.filters.useFiltersFactory}
              feature="tasks"
            />
          </div>
          <div className={styles["tabPanelWrapper"]}>
            <TabPanel
              tab={activeTabPanel.key}
              onShowTaskDetails={onShowTaskDetails}
              filters={activeTabPanel.filters}
            />
          </div>
        </>
      )}
    </div>
  );
};
